import type { DevActionableNote } from "./devActionableNoteTypes";
import { notePlacement, noteSyncBody } from "./devActionableNoteTypes";
import { DevPanelActionableNoteAssetPreview } from "./DevPanelActionableNoteAssetPreview";

type AssetInteractiveProps = {
  interactive?: boolean;
  includeInSync?: boolean;
  onIncludeInSyncChange?: (next: boolean) => void;
  onDelete?: () => void;
  deleting?: boolean;
  disabled?: boolean;
};

export function DevPanelActionableNoteMeta({
  note,
  assetInteractive,
}: {
  note: DevActionableNote;
  assetInteractive?: AssetInteractiveProps;
}) {
  const placement = notePlacement(note);
  return (
    <>
      <DevPanelActionableNoteAssetPreview note={note} {...assetInteractive} />
      {placement?.repo_path ? (
        <p className="text-[11px] text-indigo-800 dark:text-indigo-200">
          Target: <code className="rounded bg-indigo-50 px-1 dark:bg-indigo-950/50 break-all">{placement.repo_path}</code>
        </p>
      ) : null}
      {note.metadata && Object.keys(note.metadata).length ? (
        <details className="text-[11px] text-gray-600">
          <summary className="cursor-pointer font-medium text-gray-700">Metadata</summary>
          <pre className="mt-1 max-h-40 overflow-auto rounded bg-gray-50 p-2 text-[10px] dark:bg-zinc-900">
            {JSON.stringify(note.metadata, null, 2)}
          </pre>
        </details>
      ) : null}
    </>
  );
}

function noteGitBranch(note: DevActionableNote): string | null {
  const md = note.metadata as Record<string, unknown> | null | undefined;
  if (!md || typeof md !== "object") return null;
  const raw = (md as { git_branch?: unknown }).git_branch;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

export function DevPanelActionableNoteHeader({ note }: { note: DevActionableNote }) {
  const branch = noteGitBranch(note);
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide text-gray-500">
      <span
        className={
          "rounded-full border px-1.5 py-0 font-semibold " +
          (note.status === "awaiting_answer"
            ? "border-amber-300 bg-amber-50 text-amber-900"
            : note.status === "in_progress"
              ? "border-blue-300 bg-blue-50 text-blue-900"
              : "border-violet-300 bg-violet-50 text-violet-900")
        }
      >
        {note.status.replace("_", " ")}
      </span>
      {note.trust_tier === "needs_review" && note.review_status ? (
        <span className="rounded-full border border-gray-300 bg-gray-50 px-1.5 py-0 font-semibold text-gray-700">
          {note.review_status.replace("_", " ")}
        </span>
      ) : null}
      {note.trust_tier === "owner_direct" ? (
        <span className="rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-0 font-semibold text-emerald-900">
          owner direct
        </span>
      ) : null}
      {branch ? (
        <span
          className={
            "rounded-full border px-1.5 py-0 font-mono normal-case font-semibold " +
            (branch === "main"
              ? "border-slate-300 bg-slate-50 text-slate-700"
              : "border-amber-300 bg-amber-50 text-amber-900")
          }
          title="Git branch captured at submit time"
        >
          ⎇ {branch}
        </span>
      ) : null}
      <span>{new Date(note.created_at).toLocaleString()}</span>
    </div>
  );
}

export function DevPanelActionableNoteBodyPreview({
  note,
  useEffective,
}: {
  note: DevActionableNote;
  useEffective?: boolean;
}) {
  const text = useEffective ? noteSyncBody(note) : note.body;
  return (
    <>
      <p className="break-words text-sm text-gray-900 whitespace-pre-wrap dark:text-gray-100">{text}</p>
      {useEffective && note.review_status === "approved_altered" && note.body !== note.effective_body ? (
        <details className="text-[11px] text-gray-600">
          <summary className="cursor-pointer font-medium">Original submission</summary>
          <p className="mt-1 break-words whitespace-pre-wrap text-gray-700 dark:text-gray-300">{note.body}</p>
        </details>
      ) : null}
      {note.reviewer_note ? (
        <p className="text-[11px] text-gray-600 italic">Reviewer: {note.reviewer_note}</p>
      ) : null}
    </>
  );
}
